import area from '@turf/area';
import booleanWithin from '@turf/boolean-within';
import union from '@turf/union';

const SQ_METERS_PER_HECTARE = 10000;
const OVERLAP_TOLERANCE_SQ_METERS = 0.01;

const toFeature = (geometry) => ({ type: 'Feature', properties: {}, geometry });

const clampPercent = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.max(0, Math.min(100, numericValue)) : 0;
};

export class DamageGeometryError extends Error {
  constructor(code) {
    super(code);
    this.name = 'DamageGeometryError';
    this.code = code;
  }
}

const unionAllFeatures = (features) => {
  if (features.length === 0) {
    return null;
  }
  return features.slice(1).reduce((combined, feature) => {
    const combinedFeature = union(combined, feature);
    if (!combinedFeature) {
      throw new DamageGeometryError('invalid-geometry');
    }
    return combinedFeature;
  }, features[0]);
};

// area(A ∩ B) = area(A) + area(B) − area(A ∪ B).
const intersectionAreaSqMeters = (featureA, areaA, featureB, areaB) => {
  if (areaA <= 0 || areaB <= 0) {
    return 0;
  }
  const unioned = union(featureA, featureB);
  if (!unioned) {
    throw new DamageGeometryError('invalid-geometry');
  }
  const unionArea = area(unioned);
  return Math.max(0, Math.min(areaA, areaB, areaA + areaB - unionArea));
};

export const validateDamageGeometries = (aoiGeometry, spots = [], exclusions = []) => {
  if (!aoiGeometry) {
    throw new DamageGeometryError('missing-aoi');
  }

  const aoiFeature = toFeature(aoiGeometry);
  const spotFeatures = spots
    .filter((spot) => spot?.geometry)
    .map((spot) => toFeature(spot.geometry));
  const exclusionFeatures = exclusions
    .filter((exclusion) => exclusion?.geometry)
    .map((exclusion) => toFeature(exclusion.geometry));

  if (spotFeatures.some((spotFeature) => !booleanWithin(spotFeature, aoiFeature))) {
    throw new DamageGeometryError('spot-outside-aoi');
  }

  if (exclusionFeatures.some((exclusionFeature) => !booleanWithin(exclusionFeature, aoiFeature))) {
    throw new DamageGeometryError('exclusion-outside-aoi');
  }

  let spotUnionAreaSqMeters = 0;
  if (spotFeatures.length > 0) {
    const spotAreaSumSqMeters = spotFeatures.reduce((sum, spotFeature) => sum + area(spotFeature), 0);
    spotUnionAreaSqMeters = area(unionAllFeatures(spotFeatures));

    if (spotAreaSumSqMeters - spotUnionAreaSqMeters > OVERLAP_TOLERANCE_SQ_METERS) {
      throw new DamageGeometryError('overlapping-spots');
    }
  }

  const exclusionUnionFeature = exclusionFeatures.length > 0 ? unionAllFeatures(exclusionFeatures) : null;
  const exclusionUnionAreaSqMeters = exclusionUnionFeature ? area(exclusionUnionFeature) : 0;

  return { spotUnionAreaSqMeters, exclusionUnionFeature, exclusionUnionAreaSqMeters };
};

export const calculateDamageAssessment = ({
  aoiGeometry,
  spots = [],
  exclusions = [],
  remainingFieldDamagePercent = 0,
}) => {
  const {
    exclusionUnionFeature,
    exclusionUnionAreaSqMeters,
  } = validateDamageGeometries(aoiGeometry, spots, exclusions);

  const grossAoiAreaSqMeters = area(toFeature(aoiGeometry));
  const netAoiAreaSqMeters = Math.max(0, grossAoiAreaSqMeters - exclusionUnionAreaSqMeters);

  // Clip each spot to the net field: the part of a spot that overlaps a green
  // island (exclusion) is not damaged crop, so it must not be counted.
  const effectiveSpots = spots.map((spot) => {
    if (!spot?.geometry) {
      return { effectiveAreaSqMeters: 0, damagePercent: 0 };
    }
    const spotFeature = toFeature(spot.geometry);
    const grossAreaSqMeters = area(spotFeature);
    const islandOverlapSqMeters = exclusionUnionFeature
      ? intersectionAreaSqMeters(spotFeature, grossAreaSqMeters, exclusionUnionFeature, exclusionUnionAreaSqMeters)
      : 0;
    return {
      effectiveAreaSqMeters: Math.max(0, grossAreaSqMeters - islandOverlapSqMeters),
      damagePercent: clampPercent(spot.damagePercent),
    };
  });

  // Spots are validated as non-overlapping, so their effective areas simply add up.
  const effectiveSpotUnionSqMeters = Math.min(
    netAoiAreaSqMeters,
    effectiveSpots.reduce((sum, spot) => sum + spot.effectiveAreaSqMeters, 0)
  );
  const remainingAreaSqMeters = Math.max(0, netAoiAreaSqMeters - effectiveSpotUnionSqMeters);
  const spotDamageSqMeters = effectiveSpots.reduce(
    (sum, spot) => sum + spot.effectiveAreaSqMeters * (spot.damagePercent / 100),
    0
  );
  const remainingDamageSqMeters = remainingAreaSqMeters * (clampPercent(remainingFieldDamagePercent) / 100);
  const totalDamageSqMeters = Math.min(netAoiAreaSqMeters, spotDamageSqMeters + remainingDamageSqMeters);

  return {
    effectiveSpotAreasHa: effectiveSpots.map((spot) => spot.effectiveAreaSqMeters / SQ_METERS_PER_HECTARE),
    grossAoiAreaHa: grossAoiAreaSqMeters / SQ_METERS_PER_HECTARE,
    exclusionAreaHa: exclusionUnionAreaSqMeters / SQ_METERS_PER_HECTARE,
    aoiAreaHa: netAoiAreaSqMeters / SQ_METERS_PER_HECTARE,
    spotUnionAreaHa: effectiveSpotUnionSqMeters / SQ_METERS_PER_HECTARE,
    remainingAreaHa: remainingAreaSqMeters / SQ_METERS_PER_HECTARE,
    spotDamageHa: spotDamageSqMeters / SQ_METERS_PER_HECTARE,
    remainingDamageHa: remainingDamageSqMeters / SQ_METERS_PER_HECTARE,
    totalDamageHa: totalDamageSqMeters / SQ_METERS_PER_HECTARE,
    totalDamagePercent: netAoiAreaSqMeters > 0 ? (totalDamageSqMeters / netAoiAreaSqMeters) * 100 : 0,
  };
};